import React, { useCallback, useContext, useMemo, useState } from "react";
import { Button, Space, Switch, Table, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import AppContext from "../../../context/AppContext";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";

import "./accountConfig.css";

/** Browser local timezone + locale (not admin-profile timezone). */
function formatAccountConfigUpdatedAtLocal(raw: unknown): string {
  if (raw == null || raw === "") return "—";
  const d = new Date(String(raw));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
}

export type AccountConfigSnapshot = {
  meta: Record<string, Record<string, unknown>>;
  overall: Record<string, Record<string, unknown>>;
  features: Record<string, Record<string, unknown>>;
  limits: Record<string, Record<string, unknown>>;
  game_stops: Record<string, Record<string, unknown>>;
  vendors: Array<Record<string, unknown>>;
};

type ConfigRow = {
  key: string;
  config_type: string;
  sort_key: string;
  value: unknown;
  updated_at?: unknown;
  updated_by?: unknown;
};

function extractSnapshot(raw: unknown): AccountConfigSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const inner = (r.data as Record<string, unknown> | undefined) ?? r;
  if (
    inner &&
    typeof inner === "object" &&
    "meta" in inner &&
    "features" in inner &&
    "vendors" in inner
  ) {
    return inner as unknown as AccountConfigSnapshot;
  }
  const body = r.body as Record<string, unknown> | undefined;
  const fromBody = body?.data;
  if (fromBody && typeof fromBody === "object" && "meta" in fromBody) {
    return fromBody as unknown as AccountConfigSnapshot;
  }
  return null;
}

function rowsFromMap(configType: string, map: Record<string, Record<string, unknown>> | undefined): ConfigRow[] {
  if (!map || typeof map !== "object") return [];
  return Object.keys(map)
    .sort()
    .map((sort_key) => {
      const row = map[sort_key] ?? {};
      return {
        key: `${configType}:${sort_key}`,
        config_type: configType,
        sort_key,
        value: row.value,
        updated_at: row.updated_at,
        updated_by: row.updated_by,
      };
    });
}

function normalizeGameStop(val: unknown): string {
  return String(val ?? "")
    .trim()
    .toLowerCase();
}

/** Normalize for compliance labels (spaces → underscores). */
function normalizeGameStopStateKey(val: unknown): string {
  return normalizeGameStop(val).replace(/\s+/g, "_");
}

function getGameStopSwitchTooltipTitle(options: {
  disabled: boolean;
  busy: boolean;
  canEdit: boolean;
  pamToggleable: boolean;
  rawValue: unknown;
}): string | undefined {
  const { disabled, busy, canEdit, pamToggleable, rawValue } = options;
  if (!disabled) return undefined;
  if (busy) return AppStrings.ACCOUNT_CONFIG_GAME_STOP_TIP_SAVING;
  if (!canEdit) return AppStrings.ACCOUNT_CONFIG_GAME_STOP_TIP_NO_PERMISSION;
  if (!pamToggleable) {
    const k = normalizeGameStopStateKey(rawValue);
    if (k === "unapproved" || k === "not_approved") {
      return AppStrings.ACCOUNT_CONFIG_GAME_STOP_TIP_NOT_APPROVED;
    }
    if (k === "non_compliant" || k === "noncompliant") {
      return AppStrings.ACCOUNT_CONFIG_GAME_STOP_TIP_NON_COMPLIANT;
    }
    return AppStrings.ACCOUNT_CONFIG_GAME_STOP_TIP_COMPLIANCE_LOCKED;
  }
  return undefined;
}

/** PAM may toggle only `open` / `stopped`; other strings are compliance-owned (switch off, disabled). */
function isPamToggleableGameStop(val: unknown): boolean {
  const n = normalizeGameStop(val);
  return n === "open" || n === "stopped";
}

type AccountConfigAppContext = {
  adminPermissions?: string[];
};

const AccountConfig: React.FC = () => {
  const { adminPermissions } = useContext(AppContext) as AccountConfigAppContext;
  const token = JSON.parse(localStorage.getItem("token") || "null");
  const client = useMemo(
    () => createPrivateAdminPanelInstance(token?.AccessToken),
    [token?.AccessToken],
  );

  const canEdit = useMemo(() => {
    const p = adminPermissions ?? [];
    return (
      p.includes("sportsbook.configuration.account_config") ||
      p.includes("settings.account_config.update")
    );
  }, [adminPermissions]);

  const [snapshot, setSnapshot] = useState<AccountConfigSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await client.get(API_ROUTES.GET_ACCOUNT_CONFIG);
      const raw = response?.data;
      const snap = extractSnapshot(raw);
      if (!snap) {
        setLoadError(AppStrings.FAILED_TO_LOAD_ACCOUNT_CONFIG);
        setSnapshot(null);
      } else {
        setSnapshot(snap);
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, AppStrings.FAILED_TO_LOAD_ACCOUNT_CONFIG);
      setLoadError(msg);
      setSnapshot(null);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [client]);

  React.useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  const upsertRow = useCallback(
    async (config_type: string, sort_key: string, value: string | number | boolean) => {
      setSavingKey(`${config_type}:${sort_key}`);
      try {
        await client.put(API_ROUTES.UPDATE_ACCOUNT_CONFIG_ROW, {
          config_type,
          sort_key,
          value,
        });
        message.success(AppStrings.ACCOUNT_CONFIG_ROW_UPDATED);
        await fetchSnapshot();
      } catch (err) {
        message.error(getApiErrorMessage(err, AppStrings.FAILED_TO_SAVE_ACCOUNT_CONFIG_ROW));
      } finally {
        setSavingKey(null);
      }
    },
    [client, fetchSnapshot],
  );

  const gameStopColumns: ColumnsType<ConfigRow> = useMemo(() => {
    const cols: ColumnsType<ConfigRow> = [
      {
        title: "Sort key",
        dataIndex: "sort_key",
        key: "sort_key",
        width: 220,
      },
      {
        title: "Value",
        dataIndex: "value",
        key: "value",
        width: 88,
        align: "center",
        render: (val: unknown, record) => {
          const rowKey = `${record.config_type}:${record.sort_key}`;
          const busy = savingKey === rowKey;
          const norm = normalizeGameStop(val);
          const pamToggleable = isPamToggleableGameStop(val);
          const checked = norm === "open";
          const disabled = !canEdit || busy || !pamToggleable;

          const tooltipTitle = getGameStopSwitchTooltipTitle({
            disabled,
            busy,
            canEdit,
            pamToggleable,
            rawValue: val,
          });

          const switchEl = (
            <Switch
              className="account-config-game-stop-switch"
              checked={checked}
              disabled={disabled}
              onChange={(nextChecked) => {
                const nextVal = nextChecked ? "open" : "stopped";
                upsertRow(record.config_type, record.sort_key, nextVal);
              }}
            />
          );

          if (tooltipTitle) {
            return (
              <Tooltip
                title={tooltipTitle}
                classNames={{ root: "account-config-game-stop-tooltip" }}
                mouseEnterDelay={0.25}
              >
                <span className="account-config-game-stop-switch-wrap account-config-game-stop-switch-wrap--disabled">
                  {switchEl}
                </span>
              </Tooltip>
            );
          }

          return switchEl;
        },
      },
      {
        title: "Updated by",
        dataIndex: "updated_by",
        key: "updated_by",
        width: 180,
        render: (v: unknown) => (v != null && String(v).trim() !== "" ? String(v) : "—"),
      },
      {
        title: "Updated",
        key: "updated",
        width: 260,
        render: (_, record) => (
          <span className="account-config-updated-cell">
            {formatAccountConfigUpdatedAtLocal(record.updated_at)}
          </span>
        ),
      },
    ];
    return cols;
  }, [canEdit, savingKey, upsertRow]);

  const gameStopDataSource = useMemo(() => {
    if (!snapshot) return [];
    return rowsFromMap("GAME_STOP", snapshot.game_stops);
  }, [snapshot]);

  if (loading && !snapshot) {
    return (
      <div className="account-config-loading">
        <RebetLoader height="32px" width="32px" />
      </div>
    );
  }

  return (
    <div className="account-config-page">
      {loadError ? <div className="account-config-error">{loadError}</div> : null}

      <div className="account-config-toolbar">
        <Space>
          <Button onClick={() => fetchSnapshot()} disabled={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      {snapshot ? (
        <div className="account-config-game-stops">
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            columns={gameStopColumns}
            dataSource={gameStopDataSource}
            className="account-config-game-stops-table"
          />
        </div>
      ) : null}
    </div>
  );
};

export default AccountConfig;
